import { AutoComplete, ConfigProvider, Spin, Tooltip } from 'antd';
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import './customAutoComplete.less';
import { IoMdCloseCircle } from 'react-icons/io';
import ArrowSVG from '../../assets/ArrowSVG';
import { BaseOptionType, DefaultOptionType } from 'antd/lib/select';
import {
  useGetAirport,
  useGetAirportAutoComplete,
} from '@skygroup/shared/API/autoCompleteApi/autoCompleteApi';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store/store';
import {
  AUTOCOMPLETE_TYPE,
  removeDuplicateAutocomplete,
} from '@skygroup/shared/API/autoCompleteApi/autoCompleteUtils';
import { AirportElement } from './AirportElement';
import {
  AutoCompleteSheet,
  AutoCompleteTrigger,
  useAutoCompleteSheet,
} from './AutoCompleteSheet';
import { getDistanceKm } from '../../../../utils/arrivalAirport';

interface CustomAutoCompleteProps
  extends React.ComponentProps<typeof AutoComplete> {
  width?: string;
  placeholder?: string;
  /** Phones: the bottom sheet's title (default: placeholder). */
  sheetTitle?: string;
  /** Phones: hint inside the search field of the sheet / trigger. */
  searchPlaceholder?: string;
  iataCode: string | null;
  options: BaseOptionType[] | DefaultOptionType[];
  selectedAirportFromConfig?: any;
  hideArrow?: boolean;
  selectedAirport: any;
  setSelectedAirport: any;
  /** Render as a live search input from the start (with `placeholder`),
   *  instead of the selected airport's label — e.g. inside a phone sheet. */
  startInSearchMode?: boolean;
  /**
   * Shows each airport's distance from this point (the hotel, on the hotel +
   * flight pages). Cities are left without one: a metro code is not a place
   * you land at.
   */
  distanceFromPoint?: { latitude: number; longitude: number } | null;
  /** Heading over `options` while nothing has been typed yet. */
  optionsTitle?: string;
}

const CustomAutoCompleteAirport: React.FC<CustomAutoCompleteProps> = ({
  width,
  placeholder,
  sheetTitle,
  searchPlaceholder,
  iataCode,
  options,
  selectedAirportFromConfig,
  hideArrow = false,
  selectedAirport,
  setSelectedAirport,
  startInSearchMode = false,
  distanceFromPoint = null,
  optionsTitle,
  ...autoCompleteProps
}) => {
  const pointLatitude = distanceFromPoint?.latitude;
  const pointLongitude = distanceFromPoint?.longitude;
  const distanceOf = useCallback(
    (airport: any): number | null => {
      if (
        pointLatitude === undefined ||
        pointLongitude === undefined ||
        airport?.type === 'city'
      ) {
        return null;
      }
      const km = getDistanceKm(
        pointLatitude,
        pointLongitude,
        airport?.latitude,
        airport?.longitude
      );
      return km === null ? null : Math.round(km);
    },
    [pointLatitude, pointLongitude]
  );
  const { searchDatePicker: t } = useSelector(
    (state: RootState) => state.config.translations
  );
  // const { direction } = useContext(ConfigProvider.ConfigContext);
  const { direction } = useSelector((data: RootState) => data.config);
  const correctOptions = options?.map((airport: any) => {
    return {
      value: airport.value,
      location_id: airport.location_id,
      name: airport.city_name,
      airport_name: airport.name,
      latitude: airport.latitude,
      longitude: airport.longitude,
      label: (
        <AirportElement
          data={airport}
          distanceKm={distanceOf(airport)}
          // label={airport.label}
          // value={airport.value}
        />
      ),
    };
  });
  // Before anything is typed: the caller's own options, under its heading.
  const initialOptions: any[] =
    optionsTitle && correctOptions?.length
      ? [{ label: optionsTitle, options: correctOptions }]
      : correctOptions;
  const [searchValue, setSearchValue] = useState('');
  const [filteredOptions, setFilteredOptions] = useState<any[]>(initialOptions);

  const [isShowSearchValue, setIsShowSearchValue] = useState(startInSearchMode);
  // Phones: the field opens a search sheet (AutoCompleteSheet)
  const acSheet = useAutoCompleteSheet();
  const usePhoneSheet = acSheet.isPhone && !startInSearchMode;
  const openSheet = () => {
    setFilteredOptions(initialOptions);
    setSearchValue('');
    setIsShowSearchValue(true);
    acSheet.open();
  };
  const { currentLang } = useSelector((data: RootState) => data.config);

  const { data, isFetching } = useGetAirportAutoComplete(
    searchValue,
    currentLang
  );

  const { data: singleAirport, refetch: refetchSingleAirport } = useGetAirport(
    iataCode ?? ''
  );

  useEffect(() => {
    !selectedAirportFromConfig && iataCode && refetchSingleAirport();
  }, []);

  useEffect(() => {
    if (singleAirport?.data && !selectedAirport) {
      setSelectedAirport({
        value: singleAirport.data.iata_code,
        location_id: singleAirport?.data?.location_id,
        name: singleAirport?.data?.city_name,
        airport_name: singleAirport?.data?.name,
        latitude: singleAirport?.data?.latitude,
        longitude: singleAirport?.data?.longitude,
        label: (
          <AirportElement
            data={singleAirport.data}
            distanceKm={distanceOf(singleAirport.data)}
            // label={singleAirport.data.city_name}
            // value={singleAirport.data.iata_code}
          />
        ),
      });
    }
  }, [iataCode, singleAirport, distanceOf]);

  useEffect(() => {
    if (!searchValue || !data?.data) return;
    const airports = removeDuplicateAutocomplete(
      data?.data,
      AUTOCOMPLETE_TYPE.AIRPORT
    ).map((airport: any) => {
      return {
        value: airport.iata_code,
        location_id: airport.location_id,
        name: airport.city_name,
        airport_name: airport.name,
        latitude: airport.latitude,
        longitude: airport.longitude,
        label: (
          <AirportElement
            data={airport}
            distanceKm={distanceOf(airport)}
            // label={airport.city_name}
            // value={airport.iata_code}
          />
        ),
      };
    });
    setFilteredOptions(airports);
  }, [data, searchValue, distanceOf]);

  const autoCompleteEl = (
    <AutoComplete
      popupClassName={`custom-auto-complete-dropdown ${
        isFetching ? 'custom-auto-complete-loading' : ''
      } direction-${direction ?? ''}`}
      style={{ minWidth: 'unset' }}
      {...autoCompleteProps}
      dropdownMatchSelectWidth={300}
      clearIcon={<IoMdCloseCircle size={20} />}
      onSelect={(value, wholeObject) => {
        setSelectedAirport(wholeObject);
        autoCompleteProps &&
          autoCompleteProps?.onSelect?.(value, wholeObject);
        setIsShowSearchValue(false);
        setSearchValue('');
        acSheet.close();
      }}
      onFocus={() => {
        if (isShowSearchValue) {
          setSearchValue('');
          setIsShowSearchValue(true);
        }
      }}
      onSearch={(e) => {
        setIsShowSearchValue(true);
        setSearchValue(e);
      }}
      onBlur={() => {
        if (acSheet.isOpen) return; // the sheet keeps its search field
        if (startInSearchMode) return; // stays a search field
        const value = autoCompleteProps.value as string;
        setSearchValue(value);
        setIsShowSearchValue(false);
      }}
      onDropdownVisibleChange={(e) => {
        if (e) setFilteredOptions(initialOptions);
      }}
      open={usePhoneSheet ? acSheet.isOpen : autoCompleteProps.open || undefined}
      placeholder={
        usePhoneSheet
          ? (searchPlaceholder || t.searchDepartureAirport)
          : startInSearchMode
          ? placeholder
          : undefined
      }
      value={isShowSearchValue ? searchValue : undefined}
      options={filteredOptions}
      autoFocus
      {...(usePhoneSheet ? acSheet.sheetProps : {})}
    >
      {isShowSearchValue ? (
        searchValue
      ) : (
        <div
          onClick={() => {
            setIsShowSearchValue(true);
          }}
        >
          {selectedAirport?.label ?? t.searchDestination}
        </div>
      )}
    </AutoComplete>
  );

  return (
    <div className="custom-auto-complete-wrapper">
      <div className="custom-auto-complete" style={{ width }}>
        <span className="placeholder">
          {placeholder}
          {isFetching && <Spin />}
        </span>
        {usePhoneSheet ? (
          <AutoCompleteTrigger
            value={selectedAirport?.label}
            placeholder={searchPlaceholder || t.searchDepartureAirport}
            onOpen={openSheet}
          />
        ) : (
          autoCompleteEl
        )}
      </div>
      {!hideArrow && <ArrowSVG />}
      {usePhoneSheet && (
        <AutoCompleteSheet
          open={acSheet.isOpen}
          onClose={acSheet.close}
          title={sheetTitle || placeholder || t.searchDestination}
          isFetching={isFetching}
          containerRef={acSheet.containerRef}
          query={searchValue}
          resultCount={
            filteredOptions?.reduce(
              (count: number, option: any) =>
                count + (option?.options?.length ?? 1),
              0
            ) ?? 0
          }
          hint={t.acHintAirport}
          current={selectedAirport?.label}
        >
          {autoCompleteEl}
        </AutoCompleteSheet>
      )}
    </div>
  );
};

export default CustomAutoCompleteAirport;
